<template>
  <div class="block">
    <div class="block-main">
      <slot name="main-content">
      </slot>
      <div class="block-form">
        <Input
          class="block-form__input block-form__input--name"
          v-model.trim="name"
          @onBlur="$v.name.$touch"
          name="name"
          :error="nameErrorText"
          :placeholder="$t('enter_your_name')"
          :label="$t('your_name')"/>

        <AuthPhone v-if="!isUserAuth"
                   class="block-form__input block-form__input--phone"
                   :remember-field="false"
                   is-ajax-auth
                   :touch-phone-flag="touchPhoneFlag"/>
        <PhoneInput v-else
                    class="block-form__input block-form__input--phone"
                    is-label
                    disable-fields
                    v-model="phone"/>
        <EmailInput class="block-form__input block-form__input--email"
                    label="Email"
                    :email-value="email"
                    :error-text="errors.email ? $t('user_subscribes_email_form_error') : ''"
                    :placeholder="$t('enter_your_email')"
                    @getValue="updateEmail"/>
        <Button :is-disabled="isSubmiting"
                :is-native-disabled="isSubmiting"
                class="block-form__btn"
                @click.native="submitForm">
          <Loader
            class="callback-form__loader"
            v-if="isSubmiting"
            :is-loading="isSubmiting"
            :border-size="5"
            without-background
          ></Loader>
          {{ $t('find_out_first') }}
        </Button>
      </div>
    </div>
    <div class="block__img-wrap">
      <img src="https://static.dnipro-m.ua/origin/landings/podarunky/man-chainsaw.png" alt="chainsaw">
    </div>
  </div>
</template>

<script>
import Input from '@/components/inputs/Input'
import AuthPhone from '@/components/auth/AuthPhone'
import Button from '@/components/inputs/Button'
import EmailInput from '@/components/inputs/EmailInput'
import PhoneInput from '@/components/inputs/PhoneInput'
import Loader from '@/components/helpers/Loader'
import { mapState } from 'vuex'
import { minLength, required } from 'vuelidate/lib/validators'
import api from '@/api'
import qs from 'qs'

const regRus = value => !new RegExp(/[ъыёэ]/i).test(value)
const regLatin = value => !new RegExp(/[a-z]/i).test(value)
const regSpace = value => !new RegExp(/\s/g).test(value)
const regNum = value => !new RegExp(/\d/).test(value)
const regSpec = value => !new RegExp('[!@#№/$%^&*(),.?":;_=+{}|<>]').test(value)

export default {
  name: 'NewYearCallback',
  components: {
    EmailInput,
    Input,
    AuthPhone,
    PhoneInput,
    Button,
    Loader
  },
  props: {
    productId: {
      type: String,
      required: true
    }
  },
  validations: {
    name: {
      required,
      minLength: minLength(2),
      regRus,
      regLatin,
      regSpace,
      regNum,
      regSpec
    }
  },
  data () {
    return {
      phone: '',
      name: '',
      email: '',
      errors: {
        email: false
      },
      touchPhoneFlag: '',
      isSubmiting: false
    }
  },
  computed: {
    ...mapState({
      isUserAuth: state => state.user.auth,
      userData: state => state.user.user_data
    }),
    nameErrorText () {
      if (this.$v.name.$error) {
        if (!this.$v.name.minLength) {
          return this.$t('notfull_field_error')
        } else if (!this.$v.name.regRus) {
          return this.$t('name_contains_rus_letters')
        } else if (!this.$v.name.regNum) {
          return this.$t('field_contains_numbers')
        } else if (!this.$v.name.regSpace) {
          return this.$t('field_contains_special_characters')
        } else if (!this.$v.name.regLatin) {
          return this.$t('field_contains_latin_letter')
        }
        return this.$t('blank_field_error')
      } else {
        return ''
      }
    }
  },
  watch: {
    userData: {
      immediate: true,
      handler (value) {
        if (value && value.phone) {
          this.phone = value.phone.slice(3)
        }
        if (value && value.name) {
          this.name = value.name
        }
      }
    }
  },
  methods: {
    submitForm () {
      if (this.validateForm() && !this.isSubmiting) {
        this.isSubmiting = true

        let formData = {
          'OneClickForm[name]': this.name,
          'OneClickForm[email]': this.email,
          'OneClickForm[phone]': '380' + this.phone,
          'OneClickForm[productId]': this.productId,
          'OneClickForm[isCallback]': 1,
          'OneClickForm[preorder]': 0,
          'OneClickForm[callbackType]': 'technical_expert'
        }

        api.order
          .quickOrder(qs.stringify(formData))
          .then(data => {
            this.$notify({
              group: data.success ? 'success' : 'error',
              title: '',
              text: data.message,
              type: data.success ? 'success' : 'error'
            })
          })
          .catch(() => {
            this.$notify({
              group: 'error',
              title: '',
              text: this.$t('error_occurred'),
              type: 'error'
            })
          })
          .finally(() => {
            this.isSubmiting = false
          })
      }
    },
    validateForm () {
      if (!this.isUserAuth) {
        this.touchPhoneFlag = new Date().getTime().toString()
        return false
      }
      this.$v.$touch()

      return !this.$v.$invalid && !this.errors.email
    },
    updateEmail ({ isValid, value }) {
      this.errors.email = !isValid
      this.email = value
    }
  }
}
</script>

<style scoped lang="sass">
.block
  min-height: 300px
  margin-top: 150px
  padding: 35px 30px
  background-image: url('https://static.dnipro-m.ua/origin/landings/podarunky/men-bg.jpg')
  background-position: center
  background-size: cover
  background-color: #e9f2fb
  +mmin($small--hor)
    padding-bottom: 0
    display: flex
  +mmin($tablet--hor)
    padding: 60px 65px 0 65px

.block__img-wrap
  align-self: stretch
  font-size: 0
  position: relative
  flex-grow: 1
  display: none
  img
    position: absolute
    bottom: 0
    right: 0
    max-height: calc(100% + 200px)

  +mmin($small--hor)
    display: block

.block-main
  max-width: 450px
  margin: 0 auto
  +mmin($small--hor)
    max-width: 55%
    padding-bottom: 30px

.block-form ::v-deep
  label
    margin-bottom: 10px
    display: block
    color: $text-color
    font-size: 14px
    line-height: normal
  .control--input
    height: 50px
    margin-top: 0
  .control-box.control-box--phone
    width: 100%
    margin-right: 0

.block-form
  +mmin($small--hor)
    display: flex
    justify-content: space-between
    flex-wrap: wrap

.block-form__input
  margin-bottom: 20px
  &--phone
    order: 3

.block-form__btn
  height: 50px
  margin-top: 27px
  width: 100%
  order: 4

+mmin($small--hor)
  .block-form__input, .block-form__btn
    width: calc(50% - 13px)

.block__title
  margin: 0 0 25px 0
  font-size: 20px
  text-transform: uppercase
  line-height: 1.25
  font-weight: 500
  +mmin($small--hor)
    font-size: 24px
    margin: 0 0 35px 0
</style>
